import reviewsService from "@/services/reviews";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import { SENTIMENT_NAMES } from "@/enums/sentiment";

export const CATEGORIZATION_STATUSES = {
  DONE: null,
  IN_PROGRESS: 1,
  FAILED: 2
};

export default {
  data() {
    return {
      tagsState: {},
      showAlert: false,
      sentimentData: null
    };
  },

  computed: {
    activeTags() {
      return this.review.tags || [];
    },
    hasAvailableTags() {
      return !!this.availableTags.length;
    },
    hasSelectedTags() {
      return Object.values(this.tagsState).some(({ isActive }) => isActive);
    },
    categorizationInProgress() {
      return (
        this.review.categorization_status == CATEGORIZATION_STATUSES.IN_PROGRESS
      );
    },
    noTagsSelected() {
      return (
        !this.hasSelectedTags && this.hasAvailableTags && this.canTagReviews
      );
    },
    noTagsAssigned() {
      return this.selectedOnly && this.readOnly && !this.hasSelectedTags;
    },
    noTagsAvailable() {
      return (
        !this.showLoader &&
        !this.hasAvailableTags &&
        this.canTagReviews &&
        !this.readOnly
      );
    }
  },

  watch: {
    availableTags: {
      handler() {
        this.setTagsState();
      },
      immediate: true
    }
  },

  methods: {
    closeAlertDialog() {
      this.showAlert = false;
      const { tagId, sentiment } = this.sentimentData;
      const lastSentiment = this.tagsState[tagId].sentiment;

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.REVIEW_CATEGORIES_CONFIRM_AUTO_ASSIGNED_CHANGE,
        sentiment_old: SENTIMENT_NAMES[lastSentiment],
        sentiment_new: undefined,
        action: "canceled",
        category_name: this.availableTags.find(item => item.id === tagId).name,
        category_removed: sentiment ? true : false
      });
      this.sentimentData = null;
    },

    async onSentimentChange(tagId, sentiment) {
      this.review.tags = this.review.tags.map(item => {
        if (item.id === tagId) {
          return { ...item, ...sentiment };
        }
        return item;
      });
      await this.patchReviewCategory(tagId, sentiment);
    },

    async patchReviewCategory(tagId, sentiment) {
      await reviewsService.patchReviewCategory(
        this.review.id,
        tagId,
        sentiment
      );
    },

    setTagsState() {
      this.tagsState = this.availableTags.reduce((obj, tag) => {
        const localTag = this.activeTags.find(i => i.id === tag.id);
        return {
          ...obj,
          [tag.id]: {
            isActive: !!localTag,
            sentiment: localTag && localTag.sentiment,
            isAutoAssigned: localTag && localTag.is_auto_assigned
          }
        };
      }, {});
    },

    async onTagToggle(tagId, { isActive, sentiment, isAutoAssigned }) {
      this.sentimentData = { tagId, isActive, sentiment, isAutoAssigned };
      if (isActive) {
        this.review.tags.push({
          id: tagId,
          sentiment,
          is_auto_assigned: isAutoAssigned
        });
      } else {
        this.review.tags = this.review.tags.filter(item => item.id !== tagId);
      }

      if (isAutoAssigned) {
        this.showAlert = true;
        return;
      }
      await this.toggleTagHandler();
    },

    async toggleTagHandler(isDialog = false) {
      const { tagId, isActive, sentiment, isAutoAssigned } = this.sentimentData;
      const lastSentiment = this.tagsState[tagId].sentiment;
      this.tagsState[tagId] = { isActive, sentiment, isAutoAssigned };
      let categoryRemovedEvent;

      if (isActive) {
        await reviewsService.assignTag(this.review.id, tagId);
        this.tagsState[tagId].isAutoAssigned = false;
        if (sentiment !== undefined) {
          categoryRemovedEvent = false;
          await this.onSentimentChange(tagId, { sentiment });
        }
      } else {
        this.tagsState[tagId].isAutoAssigned = false;
        await reviewsService.unassignTag(this.review.id, tagId);
        categoryRemovedEvent = true;
      }

      if (isDialog) {
        this.$gtm.trackCustomEvent({
          event: GTM_EVENT_NAME.REVIEW_CATEGORIES_CONFIRM_AUTO_ASSIGNED_CHANGE,
          action: "confirmed",
          review_id: this.review.id,
          category_name: this.availableTags.find(item => item.id === tagId)
            .name,
          sentiment_old: SENTIMENT_NAMES[lastSentiment],
          sentiment_new: SENTIMENT_NAMES[sentiment],
          category_removed: categoryRemovedEvent
        });
      }

      this.$gtm.trackCustomEvent({
        event: GTM_EVENT_NAME.REVIEW_CATEGORIES_SELECT_DESELECT,
        name: this.availableTags.find(i => i.id === tagId).name,
        action: this.tagsState[tagId] ? "select" : "deselect",
        review_id: this.review.id,
        sentiment: SENTIMENT_NAMES[sentiment]
      });

      this.sentimentData = null;
      this.showAlert = false;
    }
  }
};
